import { environment } from './environments/environment';

/**
 * Общий runtime-config для main-app'а и виджета. Мутирует объект
 * `environment` из значений `/config.json`, который `docker/entrypoint.sh`
 * генерирует из ENV переменных контейнера на старте — build-once,
 * config-at-runtime модель.
 *
 * Используется одинаково:
 *   - main.ts: `await loadRuntimeConfig(); bootstrap();`
 *   - widget/web-component.ts: `await loadRuntimeConfig(); createApplication();`
 *
 * `config.json` в widget-bundle'е раздаётся с виджет-хоста (не с origin'а
 * страницы) — виджет резолвит URL через `import.meta.url`, main-app'у
 * достаточно относительного `/config.json` (тот же origin).
 */
export function applyRuntimeConfig(config: any): void {
  if (!config || typeof config !== 'object') return;

  if (config.appName) environment.appName = config.appName;
  if (config.appLandingUrl) environment.appLandingUrl = config.appLandingUrl;
  if (config.generatedApiUrl) environment.generatedApiUrl = config.generatedApiUrl;
  if (config.appUrl) environment.appUrl = config.appUrl;
  if (config.widgetUrl) environment.widgetUrl = config.widgetUrl;
  if (config.livekitUrl) environment.livekitUrl = config.livekitUrl;
  if (config.livekitSipGatewayUrl) environment.livekitSipGatewayUrl = config.livekitSipGatewayUrl;
  if (config.hashbrownProxyUrl) environment.hashbrownProxyUrl = config.hashbrownProxyUrl;
  if (config.assistantModel) environment.assistantModel = config.assistantModel;
  if (typeof config.assistantEnabled === 'boolean') {
    environment.assistantEnabled = config.assistantEnabled;
  }
  if (config.assistantRoles !== undefined && config.assistantRoles !== null) {
    const raw: string[] = Array.isArray(config.assistantRoles)
      ? config.assistantRoles.map((r: unknown) => String(r))
      : String(config.assistantRoles).split(',');
    // Trim, strip accidental wrapping quotes (e.g. ASSISTANT_ROLES="" в .env
    // arriving as the literal string '""'), drop blanks. Empty → show to all.
    environment.assistantRoles = raw
      .map((r) => r.trim().replace(/^["']+|["']+$/g, '').trim())
      .filter(Boolean);
  }
  if (config.docsUrl) environment.docsUrl = config.docsUrl;

  if (config.keycloak) {
    if (config.keycloak.url) environment.keycloak.url = config.keycloak.url;
    if (config.keycloak.realm) environment.keycloak.realm = config.keycloak.realm;
    if (config.keycloak.clientId) environment.keycloak.clientId = config.keycloak.clientId;
  }

  if (config.billing) {
    if (config.billing.url) environment.billing.url = config.billing.url;
    if (config.billing.defaultPlanCode) environment.billing.defaultPlanCode = config.billing.defaultPlanCode;
  }

  if (config.posthog) {
    if (config.posthog.key) environment.posthog.key = config.posthog.key;
    if (config.posthog.host) environment.posthog.host = config.posthog.host;
  }
}

/**
 * Fetch + apply. baseUrl:
 *   - Undefined (main-app) → `/config.json` от origin'а страницы.
 *   - Задан (widget-bundle) → `new URL('config.json', baseUrl).href`,
 *     чтобы файл резолвился рядом с бандлом на виджет-хосте, а не с
 *     origin'а страницы-хоста.
 * Ошибки/absent config — молча падаем на build-time defaults.
 */
export async function loadRuntimeConfig(baseUrl?: string): Promise<void> {
  try {
    const url = baseUrl ? new URL('config.json', baseUrl).href : '/config.json';
    const res = await fetch(url);
    if (!res.ok) return;
    const config = await res.json();
    applyRuntimeConfig(config);
  } catch {
    // config.json absent/invalid — keep build-time defaults
  }
}
